import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  Typography,
  IconButton,
  CircularProgress,
  Chip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  MenuItem,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { Edit, Close } from "@mui/icons-material";
import HeaderInternal from "../components/Header/HeaderInternal";
import { FooterInternal } from "../components/Footer";
import { FaqButton } from "../components/FaqButton";
import AddButton from "../components/AddButton";
import ResultCount from "../components/ResultCount";
import ConfirmModal from "../components/modals/ConfirmModal";
import { Toast } from "../components/Toast";
import { useToast } from "../hooks/useToast";
import { useNavigation } from "../contexts/RouterContext";
import { useAuth } from "../contexts/AuthContext";
import { colors, typography, pageHeader } from "../theme/designSystem";
import SininhoMessageEditor, { unknownSininhoVariables } from "../components/SininhoMessageEditor";
import {
  scheduleStatusesService,
  ScheduleStatusRow,
  ScheduleWindows,
  SchedulePermissionsMatrix,
  PRESENCE_KINDS,
  CONFIRMATION_KINDS,
  NOTIFY_TARGETS,
  kindLabel,
  notifyLabel,
} from "../services/scheduleStatusesService";

type Catalog = "confirmation" | "presence";

interface EditState {
  catalog: Catalog;
  row: ScheduleStatusRow;
  color: string;
  kind: string;
  notifyTarget: string;
  notifyMessage: string;
}

interface CreateState {
  catalog: Catalog;
  name: string;
  color: string;
  kind: string;
  notifyTarget: string;
  notifyMessage: string;
}

const ScheduleStatuses: React.FC = () => {
  const { user } = useAuth();
  const { goToDashboard } = useNavigation();
  const { toast, showToast, hideToast } = useToast();

  const [confirmation, setConfirmation] = useState<ScheduleStatusRow[]>([]);
  const [presence, setPresence] = useState<ScheduleStatusRow[]>([]);
  const [loading, setLoading] = useState(true);

  const [editing, setEditing] = useState<EditState | null>(null);
  const [creating, setCreating] = useState<CreateState | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [chargeTarget, setChargeTarget] = useState<ScheduleStatusRow | null>(null);
  const [terminalTarget, setTerminalTarget] = useState<ScheduleStatusRow | null>(null);
  const [saving, setSaving] = useState(false);

  // Prazos da agenda (RN-118k). `windows` é o que está gravado; os campos guardam o texto
  // digitado para o usuário poder apagar e redigitar sem o valor saltar para 1 no meio.
  const [windows, setWindows] = useState<ScheduleWindows | null>(null);
  // Permissões da agenda por perfil (RN-115d): carregada à parte — se falhar, o resto da tela segue
  const [matrix, setMatrix] = useState<SchedulePermissionsMatrix | null>(null);
  const [savingCell, setSavingCell] = useState<string | null>(null);
  const [editDays, setEditDays] = useState("");
  const [retroDays, setRetroDays] = useState("");
  const [savingWindows, setSavingWindows] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [data, janelas] = await Promise.all([
        scheduleStatusesService.getAll(),
        scheduleStatusesService.getWindows(),
      ]);
      setConfirmation(data.confirmation);
      setPresence(data.presence);
      setWindows(janelas);
      scheduleStatusesService.getPermissions().then(setMatrix).catch(() => setMatrix(null));
      setEditDays(String(janelas.editLockDays));
      setRetroDays(String(janelas.retroactiveDays));
    } catch {
      showToast("Erro ao carregar as configurações da agenda.", "error");
    } finally {
      setLoading(false);
    }
  };

  const saveWindows = async () => {
    const edit = Number(editDays);
    const retro = Number(retroDays);
    // Mesma faixa do servidor: a tela avisa antes de ir e volta, o servidor decide de verdade
    if (!Number.isInteger(edit) || !Number.isInteger(retro)
        || edit < 1 || retro < 1 || edit > 1095 || retro > 1095) {
      showToast("Informe um número inteiro de 1 a 1095 dias nos dois campos.", "error");
      return;
    }
    setSavingWindows(true);
    try {
      await scheduleStatusesService.setWindows({ editLockDays: edit, retroactiveDays: retro });
      setWindows({ editLockDays: edit, retroactiveDays: retro });
      showToast("Prazos da agenda salvos.", "success");
    } catch {
      showToast("Erro ao salvar os prazos da agenda.", "error");
    } finally {
      setSavingWindows(false);
    }
  };

  useEffect(() => {
    document.title = "Clinic4Us - Status de agendamento";
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // O seletor nativo de cor dispara `input`/`change` de forma diferente por navegador
  // (Chrome ao vivo, Firefox ao fechar o diálogo do SO). Update funcional + captura dos
  // dois eventos garante que a cor escolhida não se perca ao fechar o seletor.
  const setEditingColor = (value: string) =>
    setEditing((prev) => (prev ? { ...prev, color: value } : prev));

  const openEdit = (catalog: Catalog, row: ScheduleStatusRow) => {
    setEditing({ catalog, row, color: row.color || "#708090", kind: row.kind ?? "", notifyTarget: row.notifyTarget ?? "", notifyMessage: row.notifyMessage ?? "" });
  };

  // A edição altera só a COR; ativo e cobra atendimento são alternados pela tag da lista
  // (padrão do sistema). Salvar a cor pede confirmação: reflete em todo o histórico.
  const confirmSave = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      if (editing.catalog === "confirmation") {
        await scheduleStatusesService.updateConfirmation(editing.row.id, {
          color: editing.color || null,
          active: editing.row.active,
          isTerminal: editing.row.isTerminal ?? false,
          kind: editing.kind || null,
          notifyTarget: editing.notifyTarget,
          notifyMessage: editing.notifyMessage,
        });
      } else {
        await scheduleStatusesService.updatePresence(editing.row.id, {
          color: editing.color || null,
          active: editing.row.active,
          chargeForService: editing.row.chargeForService ?? false,
          kind: editing.kind || null,
          notifyTarget: editing.notifyTarget,
          notifyMessage: editing.notifyMessage,
        });
      }
      showToast("Status atualizado.", "success");
      setConfirmOpen(false);
      setEditing(null);
      await load();
    } catch (e: any) {
      showToast(e?.response?.data?.description || "Erro ao salvar o status.", "error");
    } finally {
      setSaving(false);
    }
  };

  // Ativo/Inativo alterna direto pela tag (padrão do sistema, sem modal): inativar não
  // reescreve o histórico, só tira o status das opções futuras da agenda.
  const toggleActive = async (catalog: Catalog, row: ScheduleStatusRow) => {
    try {
      if (catalog === "confirmation") {
        await scheduleStatusesService.updateConfirmation(row.id, {
          color: row.color,
          active: !row.active,
          isTerminal: row.isTerminal ?? false,
          kind: row.kind ?? null,
        });
      } else {
        await scheduleStatusesService.updatePresence(row.id, {
          color: row.color,
          active: !row.active,
          chargeForService: row.chargeForService ?? false,
          kind: row.kind ?? null,
        });
      }
      await load();
    } catch {
      showToast("Erro ao alterar a disponibilidade do status.", "error");
    }
  };

  // Permite evolução alterna direto pela tag, como Ativo/Inativo: só muda quais sessões o
  // formulário de evolução oferece daqui para frente; evolução já gravada não muda (RN-542i)
  const toggleEvolution = async (catalog: Catalog, row: ScheduleStatusRow) => {
    try {
      const allowsEvolution = !(row.allowsEvolution ?? true);
      if (catalog === "confirmation") {
        await scheduleStatusesService.updateConfirmation(row.id, {
          color: row.color,
          active: row.active,
          isTerminal: row.isTerminal ?? false,
          kind: row.kind ?? null,
          allowsEvolution,
        });
      } else {
        await scheduleStatusesService.updatePresence(row.id, {
          color: row.color,
          active: row.active,
          chargeForService: row.chargeForService ?? false,
          kind: row.kind ?? null,
          allowsEvolution,
        });
      }
      await load();
    } catch {
      showToast("Erro ao alterar se o status permite evolução.", "error");
    }
  };

  // Cobra atendimento muda o que entra como recebível nos relatórios (inclusive histórico),
  // por isso pede confirmação — diferente do Ativo/Inativo.
  const confirmToggleCharge = async () => {
    if (!chargeTarget) return;
    setSaving(true);
    try {
      await scheduleStatusesService.updatePresence(chargeTarget.id, {
        color: chargeTarget.color,
        active: chargeTarget.active,
        chargeForService: !(chargeTarget.chargeForService ?? false),
        kind: chargeTarget.kind ?? null,
      });
      showToast("Status atualizado.", "success");
      setChargeTarget(null);
      await load();
    } catch {
      showToast("Erro ao salvar o status.", "error");
    } finally {
      setSaving(false);
    }
  };

  // Finalizador muda o comportamento da agenda (presença bloqueada com o status aplicado),
  // por isso pede confirmação — mesmo padrão do cobra atendimento.
  const confirmToggleTerminal = async () => {
    if (!terminalTarget) return;
    setSaving(true);
    try {
      await scheduleStatusesService.updateConfirmation(terminalTarget.id, {
        color: terminalTarget.color,
        active: terminalTarget.active,
        isTerminal: !(terminalTarget.isTerminal ?? false),
        kind: terminalTarget.kind ?? null,
      });
      showToast("Status atualizado.", "success");
      setTerminalTarget(null);
      await load();
    } catch {
      showToast("Erro ao salvar o status.", "error");
    } finally {
      setSaving(false);
    }
  };

  const openCreate = (catalog: Catalog) => {
    setCreating({ catalog, name: "", color: "#708090", kind: "", notifyTarget: "", notifyMessage: "" });
  };

  const setCreatingColor = (value: string) =>
    setCreating((prev) => (prev ? { ...prev, color: value } : prev));

  // Inclusão é aditiva (não altera o histórico), então não pede a confirmação de descasamento.
  const confirmCreate = async () => {
    if (!creating) return;
    if (!creating.name.trim()) {
      showToast("Informe o nome do status.", "error");
      return;
    }
    setSaving(true);
    try {
      // Novo status nasce ativo e sem cobrar; ativo e cobra atendimento se ajustam pela lista.
      if (creating.catalog === "confirmation") {
        await scheduleStatusesService.createConfirmation({
          name: creating.name.trim(),
          color: creating.color || null,
          active: true,
          isTerminal: false,
          kind: creating.kind || null,
          notifyTarget: creating.notifyTarget || null,
          notifyMessage: creating.notifyMessage || null,
        });
      } else {
        await scheduleStatusesService.createPresence({
          name: creating.name.trim(),
          color: creating.color || null,
          active: true,
          chargeForService: false,
          kind: creating.kind || null,
          notifyTarget: creating.notifyTarget || null,
          notifyMessage: creating.notifyMessage || null,
        });
      }
      showToast("Status incluído.", "success");
      setCreating(null);
      await load();
    } catch (e: any) {
      const msg = e?.response?.data?.description || "Erro ao incluir o status.";
      showToast(msg, "error");
    } finally {
      setSaving(false);
    }
  };

  // Prazos da agenda (RN-118k): primeira seção do painel, antes dos catálogos — é a regra que
  // governa a agenda, enquanto os status são vocabulário. Dois campos, um Salvar próprio: são
  // independentes das listas e gravam noutro endpoint.
  const alterado = windows !== null
    && (String(windows.editLockDays) !== editDays.trim() || String(windows.retroactiveDays) !== retroDays.trim());
  // Retroativo maior que a edição é PERMITIDO (pode ser intencional: lançar histórico sem
  // reabrir o passado), mas cria agendamento que já nasce travado — a tela avisa, não impede.
  const retroAlemDaEdicao = Number(retroDays) > Number(editDays);

  const renderWindows = () => (
    <div style={{ marginBottom: "1.5rem" }}>
      <div className="c4u-filters__row">
        <div className="c4u-filters__fields">
          <Typography sx={{ fontWeight: 600, color: colors.textPrimary, alignSelf: "center" }}>
            Prazos de edição
          </Typography>
        </div>
      </div>

      <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 1.5 }}>
        Contados em dias corridos, no fuso da clínica.
      </Typography>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "flex-start" }}>
        <TextField
          label="Edição do agendamento"
          value={editDays}
          onChange={(e) => setEditDays(e.target.value.replace(/\D/g, "").slice(0, 4))}
          helperText="Dias após a data do atendimento em que ele ainda pode ser alterado ou excluído."
          inputProps={{ inputMode: "numeric", maxLength: 4 }}
          sx={{ width: { xs: "100%", sm: 260 } }}
        />
        <TextField
          label="Lançamento retroativo"
          value={retroDays}
          onChange={(e) => setRetroDays(e.target.value.replace(/\D/g, "").slice(0, 4))}
          helperText="Até quantos dias para trás um agendamento pode ser criado ou movido."
          inputProps={{ inputMode: "numeric", maxLength: 4 }}
          sx={{ width: { xs: "100%", sm: 260 } }}
        />
        <Button
          variant="contained"
          onClick={saveWindows}
          disabled={!alterado || savingWindows}
          sx={{ mt: { xs: 0, sm: 1 }, minWidth: 120 }}
        >
          {savingWindows ? "Salvando..." : "Salvar"}
        </Button>
      </Box>

      {retroAlemDaEdicao && (
        <Typography variant="body2" sx={{ color: colors.warningSoftText, mt: 1.5 }}>
          O prazo retroativo é maior que o de edição: um agendamento lançado além de{" "}
          {editDays} dias nasce já bloqueado para alteração. Use assim só se a intenção for
          registrar histórico sem permitir correções.
        </Typography>
      )}
    </div>
  );

  // Rótulo curto da coluna; o título completo do catálogo vai na dica
  const PERMISSION_SHORT: Record<string, string> = {
    "schedule-create": "Inserir",
    "schedule-edit": "Editar",
    "schedule-delete": "Excluir",
    "schedule-attendance": "Confirmação e presença",
    "schedule-block-edit": "Criar e editar bloqueio",
    "schedule-block-delete": "Excluir bloqueio",
  };

  // Marca/desmarca na hora, com volta atrás se a gravação falhar
  const togglePermission = async (profileId: string, permission: string, granted: boolean) => {
    if (!matrix) return;
    const key = `${profileId}:${permission}`;
    const apply = (on: boolean) => setMatrix((m) => m && {
      ...m,
      profiles: m.profiles.map((r) => r.profileId !== profileId ? r : {
        ...r,
        granted: on ? Array.from(new Set([...r.granted, permission])) : r.granted.filter((g) => g !== permission),
      }),
    });
    setSavingCell(key);
    apply(granted);
    try {
      await scheduleStatusesService.setPermission(profileId, permission, granted);
    } catch {
      apply(!granted);
      showToast("Erro ao salvar a permissão.", "error");
    } finally {
      setSavingCell(null);
    }
  };

  const renderPermissions = () => {
    if (!matrix || matrix.permissions.length === 0) return null;
    const columns = `minmax(160px, 1.3fr) repeat(${matrix.permissions.length}, minmax(0, 1fr))`;
    return (
      <div style={{ marginBottom: "1.5rem" }}>
        <div className="c4u-filters__row">
          <div className="c4u-filters__fields">
            <Typography sx={{ fontWeight: 600, color: colors.textPrimary, alignSelf: "center" }}>
              Permissões da agenda
            </Typography>
          </div>
        </div>
        <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 1.5, maxWidth: 760 }}>
          O que cada perfil faz na agenda. Quem só tem "Confirmação e presença" altera o status do agendamento, sem mudar
          data, horário ou profissional. O alcance (só a própria agenda ou a clínica toda) continua valendo. A mudança vale
          no próximo login dos usuários do perfil.
        </Typography>

        <Box sx={{ border: "1px solid #e2e8f0", borderRadius: "8px", overflow: "hidden" }}>
          <Box sx={{
            display: { xs: "none", md: "grid" }, gridTemplateColumns: columns, columnGap: 1,
            px: 2, py: 1.25, backgroundColor: "#f8f9fa", borderBottom: "1px solid #e2e8f0",
          }}>
            <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: colors.textPrimary }}>Perfil</Typography>
            {matrix.permissions.map((c) => (
              <Typography key={c.permission} title={c.title}
                sx={{ fontSize: "0.8rem", fontWeight: 600, color: colors.textPrimary, textAlign: "center", lineHeight: 1.3 }}>
                {PERMISSION_SHORT[c.permission] ?? c.title}
              </Typography>
            ))}
          </Box>
          {matrix.profiles.map((row, i) => (
            <Box key={row.profileId} sx={{
              display: { xs: "block", md: "grid" }, gridTemplateColumns: columns, columnGap: 1, alignItems: "center",
              px: 2, py: { xs: 1.5, md: 0.5 },
              borderTop: i === 0 ? "none" : "1px solid #edf2f7",
            }}>
              <Typography sx={{ fontWeight: 600, fontSize: "0.9rem", color: colors.textPrimary, mb: { xs: 0.5, md: 0 } }}>
                {row.name}
              </Typography>
              <Box sx={{ display: { xs: "grid", md: "contents" }, gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(3, 1fr)" }, columnGap: 1 }}>
                {matrix.permissions.map((c) => {
                  const checked = row.granted.includes(c.permission);
                  const label = PERMISSION_SHORT[c.permission] ?? c.title;
                  return (
                    <FormControlLabel
                      key={c.permission}
                      sx={{
                        m: 0, justifyContent: { md: "center" },
                        "& .MuiFormControlLabel-label": { display: { xs: "inline", md: "none" }, fontSize: "0.85rem", color: colors.textSecondary },
                      }}
                      control={
                        <Checkbox
                          checked={checked}
                          disabled={savingCell === `${row.profileId}:${c.permission}`}
                          onChange={(e) => togglePermission(row.profileId, c.permission, e.target.checked)}
                          inputProps={{ "aria-label": `${label} — ${row.name}` }}
                          sx={{ color: colors.primary, "&.Mui-checked": { color: colors.primary } }}
                        />
                      }
                      label={label}
                    />
                  );
                })}
              </Box>
            </Box>
          ))}
        </Box>
      </div>
    );
  };

  const renderList = (
    catalog: Catalog, rows: ScheduleStatusRow[], title: string, withCharge: boolean, withTerminal: boolean) => (
    <div style={{ marginBottom: "1.5rem" }}>
      <div className="c4u-filters__row">
        <div className="c4u-filters__fields">
          <Typography sx={{ fontWeight: 600, color: colors.textPrimary, alignSelf: "center" }}>
            {title}
          </Typography>
        </div>
        <div className="c4u-filters__actions">
          <AddButton onClick={() => openCreate(catalog)} title="Incluir status" />
        </div>
      </div>

      <ResultCount shown={rows.length} />

      <Box className="admin-plans-list-container" sx={{ background: "none", border: "none", borderRadius: 0, boxShadow: "none" }}>
        <Box className="admin-plans-table admin-entities-table">
          <Box className="c4u-table__header">
            <Box className="admin-plans-header-cell" sx={{ flex: "0 0 60px", textAlign: "left" }}>Cor</Box>
            <Box className="admin-plans-header-cell" sx={{ flex: "1 1 auto", minWidth: 160, textAlign: "left" }}>Status</Box>
            <Box className="admin-plans-header-cell" sx={{ flex: "0 0 220px", textAlign: "left" }}>Significado</Box>
            <Box className="admin-plans-header-cell c4u-col--md" sx={{ flex: "0 0 170px", textAlign: "left" }}>Avisa pelo chat</Box>
            {withCharge && <Box className="admin-plans-header-cell" sx={{ flex: "0 0 150px", textAlign: "left" }}>Cobra atendimento</Box>}
            {withTerminal && <Box className="admin-plans-header-cell" sx={{ flex: "0 0 120px", textAlign: "left" }}>Finalizador</Box>}
            <Box className="admin-plans-header-cell" sx={{ flex: "0 0 140px", textAlign: "left" }}>Permite evolução</Box>
            <Box className="admin-plans-header-cell" sx={{ flex: "0 0 100px", textAlign: "left" }}>Status</Box>
            <Box className="admin-plans-header-cell" sx={{ flex: "0 0 80px", justifyContent: "flex-end" }}>Ações</Box>
          </Box>

          <Box className="admin-plans-table-body">
            {rows.map((row) => (
              <Box
                key={row.id}
                className="c4u-table__row"
              >
                <Box className="admin-plans-cell" sx={{ flex: "0 0 60px", textAlign: "left" }} data-label="Cor">
                  <Box sx={{ width: 24, height: 24, borderRadius: "4px", border: "1px solid #ced4da", background: row.color || "#e9ecef" }} />
                </Box>
                <Box className="admin-plans-cell" sx={{ flex: "1 1 auto", minWidth: 160, textAlign: "left", fontWeight: 600, whiteSpace: "normal", wordBreak: "break-word" }} data-label="Status">
                  {row.name}
                </Box>
                <Box className="admin-plans-cell" sx={{ flex: "0 0 220px", textAlign: "left", fontSize: "0.85rem" }} data-label="Significado">
                  {kindLabel(catalog, row.kind)}
                </Box>
                <Box className="admin-plans-cell c4u-col--md" sx={{ flex: "0 0 170px", textAlign: "left", fontSize: "0.85rem" }} data-label="Avisa pelo chat">
                  {notifyLabel(row.notifyTarget)}
                </Box>
                {withCharge && (
                  <Box className="admin-plans-cell" sx={{ flex: "0 0 150px", textAlign: "left" }} data-label="Cobra atendimento">
                    <Chip
                      label={row.chargeForService ? "Sim" : "Não"}
                      size="small"
                      onClick={() => setChargeTarget(row)}
                      title="Clique para alternar se cobra atendimento"
                      sx={{ color: "white", fontWeight: 600, cursor: "pointer", backgroundColor: row.chargeForService ? "#28a745" : "#adb5bd" }}
                    />
                  </Box>
                )}
                {withTerminal && (
                  <Box className="admin-plans-cell" sx={{ flex: "0 0 120px", textAlign: "left" }} data-label="Finalizador">
                    <Chip
                      label={row.isTerminal ? "Sim" : "Não"}
                      size="small"
                      onClick={(e) => { e.stopPropagation(); setTerminalTarget(row); }}
                      title="Clique para alternar se o status é finalizador"
                      sx={{ color: "white", fontWeight: 600, cursor: "pointer", backgroundColor: row.isTerminal ? "#dc3545" : "#adb5bd" }}
                    />
                  </Box>
                )}
                <Box className="admin-plans-cell" sx={{ flex: "0 0 140px", textAlign: "left" }} data-label="Permite evolução">
                  <Chip
                    label={(row.allowsEvolution ?? true) ? "Sim" : "Não"}
                    size="small"
                    onClick={(e) => { e.stopPropagation(); toggleEvolution(catalog, row); }}
                    title="Clique para alternar se a sessão neste status aparece no formulário de evolução"
                    sx={{ color: "white", fontWeight: 600, cursor: "pointer", backgroundColor: (row.allowsEvolution ?? true) ? "#28a745" : "#adb5bd" }}
                  />
                </Box>
                <Box className="admin-plans-cell" sx={{ flex: "0 0 100px", textAlign: "left" }} data-label="Status">
                  <Chip
                    label={row.active ? "Ativo" : "Inativo"}
                    size="small"
                    onClick={(e) => { e.stopPropagation(); toggleActive(catalog, row); }}
                    title={row.active ? "Clique para inativar" : "Clique para ativar"}
                    sx={{ color: "white", fontWeight: 600, cursor: "pointer", backgroundColor: row.active ? "#28a745" : "#adb5bd" }}
                  />
                </Box>
                <Box className="admin-plans-cell admin-plans-actions" sx={{ flex: "0 0 80px", textAlign: "right" }} data-label="Ações">
                  <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                    <IconButton
                      className="c4u-row-action"
                      size="small"
                      onClick={(e) => { e.stopPropagation(); openEdit(catalog, row); }}
                      title="Editar"
                      sx={{ backgroundColor: "#03B4C6", "&:hover": { backgroundColor: "#029AAB" } }}
                    >
                      <Edit sx={{ fontSize: "1rem" }} />
                    </IconButton>
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </div>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <HeaderInternal
        showCTAButton={false}
        className="login-header"
        isLoggedIn={true}
        userEmail={user?.email || ""}
        userProfile={user?.role || ""}
        clinicName={user?.clinicName || "Clínica"}
        notificationCount={0}
        onRevalidateLogin={() => {}}
        onNotificationClick={() => {}}
        onUserClick={() => {}}
        onLogoClick={goToDashboard}
      />

      <Box component="main" sx={{ padding: "1rem", minHeight: "calc(100vh - 120px)", background: colors.background, marginTop: "85px", flex: 1 }}>
        <Container maxWidth={false} disableGutters sx={pageHeader.container}>
          <Box className="c4u-page-header c4u-page-header--in-panel">
            <Box>
              <Typography variant="h4" className="c4u-page-header__title">Configurações da agenda
              </Typography>
              <Typography variant="body2" className="c4u-page-header__subtitle">Prazos de edição e catálogos de status usados na agenda.
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <FaqButton />
            </Box>
          </Box>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress sx={{ color: colors.primary }} />
            </Box>
          ) : (
            <>
              {/* Painel único: prazos e as duas listas são seções do mesmo bloco */}
              <div className="c4u-panel">
                {renderWindows()}
                {renderList("confirmation", confirmation, "Status de confirmação", false, true)}
                {renderList("presence", presence, "Status de presença", true, false)}
                {renderPermissions()}
              </div>
            </>
          )}
        </Container>
      </Box>

      {/* Edição: cor + ativo (+ cobra atendimento na presença); nome não é editável */}
      <Dialog open={editing !== null} onClose={() => setEditing(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: "12px" } }}>
        <DialogTitle sx={{ backgroundColor: colors.primary, color: colors.white, display: "flex", justifyContent: "space-between", alignItems: "center", padding: "1rem 1.25rem" }}>
          <Typography component="h3" sx={{ fontSize: "1.1rem", fontWeight: typography.fontWeight.semibold, margin: 0 }}>Editar status</Typography>
          <IconButton onClick={() => setEditing(null)} sx={{ color: colors.white, padding: "0.25rem" }}><Close /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: "1.25rem !important", paddingTop: "1.5rem !important" }}>
          {editing && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField label="Status" value={editing.row.name} disabled fullWidth size="small" InputLabelProps={{ shrink: true }} />
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <input
                  type="color"
                  value={/^#[0-9a-fA-F]{6}$/.test(editing.color) ? editing.color : "#708090"}
                  onChange={(e) => setEditingColor(e.target.value)}
                  onInput={(e) => setEditingColor((e.target as HTMLInputElement).value)}
                  style={{ width: 48, height: 40, border: "1px solid #ced4da", borderRadius: 4, background: "none", cursor: "pointer" }}
                  aria-label="Cor do status"
                />
                <TextField
                  label="Cor (hex)"
                  value={editing.color}
                  onChange={(e) => setEditingColor(e.target.value)}
                  size="small"
                  fullWidth
                  inputProps={{ maxLength: 7 }}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
              <TextField
                select
                label="Significado para o sistema"
                value={editing.kind}
                onChange={(e) => setEditing((prev) => (prev ? { ...prev, kind: e.target.value } : prev))}
                size="small"
                fullWidth
                helperText="O nome é livre; é o significado que os relatórios, a agenda e as oportunidades reconhecem."
                InputLabelProps={{ shrink: true }}
              >
                {(editing.catalog === "presence" ? PRESENCE_KINDS : CONFIRMATION_KINDS).map((k) => (
                  <MenuItem key={k.value || "none"} value={k.value}>{k.label}</MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Avisar pelo chat (Sininho)"
                value={editing.notifyTarget}
                onChange={(e) => setEditing((prev) => (prev ? { ...prev, notifyTarget: e.target.value } : prev))}
                size="small"
                fullWidth
                helperText="Quando um agendamento entra neste status, o Sininho - Recepção Digital manda o aviso com o botão para abrir a ficha do paciente."
                InputLabelProps={{ shrink: true }}
              >
                {NOTIFY_TARGETS.map((t) => (
                  <MenuItem key={t.value || "none"} value={t.value}>{t.label}</MenuItem>
                ))}
              </TextField>
              {editing.notifyTarget && (
                <SininhoMessageEditor
                  value={editing.notifyMessage}
                  onChange={(v) => setEditing((prev) => (prev ? { ...prev, notifyMessage: v } : prev))}
                  statusName={editing.row.name}
                />
              )}
              <Typography sx={{ fontSize: "0.8rem", color: colors.textSecondary }}>
                Ativar/inativar e cobra atendimento se alternam pela tag na lista.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ padding: "1rem 1.25rem" }}>
          <Button onClick={() => setEditing(null)} variant="outlined" sx={{ textTransform: "none", color: "#6c757d", borderColor: "#6c757d" }}>Cancelar</Button>
          <Button onClick={() => setConfirmOpen(true)} disabled={!!editing && unknownSininhoVariables(editing.notifyMessage).length > 0} variant="contained" sx={{ textTransform: "none", backgroundColor: colors.primary }}>Salvar</Button>
        </DialogActions>
      </Dialog>

      {/* Inclusão: nome editável + cor + ativo (+ cobra atendimento na presença) */}
      <Dialog open={creating !== null} onClose={() => setCreating(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: "12px" } }}>
        <DialogTitle sx={{ backgroundColor: colors.primary, color: colors.white, display: "flex", justifyContent: "space-between", alignItems: "center", padding: "1rem 1.25rem" }}>
          <Typography component="h3" sx={{ fontSize: "1.1rem", fontWeight: typography.fontWeight.semibold, margin: 0 }}>
            {creating?.catalog === "presence" ? "Incluir status de presença" : "Incluir status de confirmação"}
          </Typography>
          <IconButton onClick={() => setCreating(null)} sx={{ color: colors.white, padding: "0.25rem" }}><Close /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: "1.25rem !important", paddingTop: "1.5rem !important" }}>
          {creating && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                label="Nome do status"
                value={creating.name}
                onChange={(e) => setCreating({ ...creating, name: e.target.value })}
                fullWidth
                size="small"
                autoFocus
                helperText={`${creating.name.length}/36 — o nome é gravado em cada agendamento`}
                // 36 = largura do campo de status no agendamento; maior truncaria e quebraria
                // o casamento por nome dos relatórios (o servidor também valida)
                inputProps={{ maxLength: 36 }}
                InputLabelProps={{ shrink: true }}
              />
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <input
                  type="color"
                  value={/^#[0-9a-fA-F]{6}$/.test(creating.color) ? creating.color : "#708090"}
                  onChange={(e) => setCreatingColor(e.target.value)}
                  onInput={(e) => setCreatingColor((e.target as HTMLInputElement).value)}
                  style={{ width: 48, height: 40, border: "1px solid #ced4da", borderRadius: 4, background: "none", cursor: "pointer" }}
                  aria-label="Cor do status"
                />
                <TextField
                  label="Cor (hex)"
                  value={creating.color}
                  onChange={(e) => setCreatingColor(e.target.value)}
                  size="small"
                  fullWidth
                  inputProps={{ maxLength: 7 }}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
              <TextField
                select
                label="Significado para o sistema"
                value={creating.kind}
                onChange={(e) => setCreating((prev) => (prev ? { ...prev, kind: e.target.value } : prev))}
                size="small"
                fullWidth
                helperText="Escolha o que este status significa; sem significado ele só marca a agenda."
                InputLabelProps={{ shrink: true }}
              >
                {(creating.catalog === "presence" ? PRESENCE_KINDS : CONFIRMATION_KINDS).map((k) => (
                  <MenuItem key={k.value || "none"} value={k.value}>{k.label}</MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Avisar pelo chat (Sininho)"
                value={creating.notifyTarget}
                onChange={(e) => setCreating((prev) => (prev ? { ...prev, notifyTarget: e.target.value } : prev))}
                size="small"
                fullWidth
                helperText="Quando um agendamento entra neste status, o Sininho - Recepção Digital manda o aviso com o botão para abrir a ficha do paciente."
                InputLabelProps={{ shrink: true }}
              >
                {NOTIFY_TARGETS.map((t) => (
                  <MenuItem key={t.value || "none"} value={t.value}>{t.label}</MenuItem>
                ))}
              </TextField>
              {creating.notifyTarget && (
                <SininhoMessageEditor
                  value={creating.notifyMessage}
                  onChange={(v) => setCreating((prev) => (prev ? { ...prev, notifyMessage: v } : prev))}
                  statusName={creating.name.trim() || "Novo status"}
                />
              )}
              <Typography sx={{ fontSize: "0.8rem", color: colors.textSecondary }}>
                O status nasce ativo. Ativar/inativar{creating.catalog === "presence" ? " e cobra atendimento" : ""} se alternam pela tag na lista.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ padding: "1rem 1.25rem" }}>
          <Button onClick={() => setCreating(null)} variant="outlined" sx={{ textTransform: "none", color: "#6c757d", borderColor: "#6c757d" }}>Cancelar</Button>
          <Button onClick={confirmCreate} variant="contained" disabled={saving} sx={{ textTransform: "none", backgroundColor: colors.primary }}>
            {saving ? "Salvando..." : "Incluir"}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmModal
        isOpen={confirmOpen}
        title="Confirmar edição"
        message="Esta alteração passa a valer para TODO o histórico de agendamentos — os agendamentos passados serão exibidos com a nova configuração deste status."
        warningMessage="Pode causar descasamento com o que estava registrado em cada agendamento antigo. Confirme apenas se tiver certeza."
        type="warning"
        confirmButtonText="Salvar mesmo assim"
        cancelButtonText="Cancelar"
        isProcessing={saving}
        onConfirm={confirmSave}
        onCancel={() => setConfirmOpen(false)}
      />

      <ConfirmModal
        isOpen={terminalTarget !== null}
        title="Alterar status finalizador"
        message={`Tornar "${terminalTarget?.name ?? ""}" ${terminalTarget?.isTerminal ? "NÃO finalizador libera" : "finalizador bloqueia"} a marcação de presença nos agendamentos com este status de confirmação. Cancelamento em cima da hora que deve ser cobrado usa a presença "Não compareceu - Não avisou", sem status de cancelamento.`}
        warningMessage="A trava vale para todos que editam a agenda. Confirme apenas se tiver certeza."
        type="warning"
        confirmButtonText="Alterar mesmo assim"
        cancelButtonText="Cancelar"
        isProcessing={saving}
        onConfirm={confirmToggleTerminal}
        onCancel={() => setTerminalTarget(null)}
      />

      <ConfirmModal
        isOpen={chargeTarget !== null}
        title="Alterar cobra atendimento"
        message={`Alterar "cobra atendimento" de "${chargeTarget?.name ?? ""}" muda o que entra como recebível nos relatórios — inclusive nos agendamentos passados com este status de presença.`}
        warningMessage="A apuração financeira dos relatórios passa a considerar este status de forma diferente. Confirme apenas se tiver certeza."
        type="warning"
        confirmButtonText="Alterar mesmo assim"
        cancelButtonText="Cancelar"
        isProcessing={saving}
        onConfirm={confirmToggleCharge}
        onCancel={() => setChargeTarget(null)}
      />

      <FooterInternal className="login-footer-component" />
      <Toast message={toast.message} type={toast.type} isVisible={toast.isVisible} onClose={hideToast} />
    </Box>
  );
};

export default ScheduleStatuses;
